/**
 * AppShell — the standard 2-column layout used by every authenticated
 * page (except focus-mode Task Page and full-bleed auth screens).
 *
 * Desktop (≥lg) sidebar modes (persisted, toggled from the Topbar):
 *   open   — 240px labelled rail
 *   icons  — 76px icon rail
 *   closed — hidden, content full-width
 *
 * Below lg the sidebar is always a slide-over drawer (full variant)
 * toggled from the Topbar hamburger; it closes on navigation.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import Topbar from './Topbar.jsx';
import OnboardingTour from './OnboardingTour.jsx';
import { useSidebarMode } from '../../hooks/useSidebarMode.js';

const MODE_WIDTH = { open: '240px', icons: '76px', closed: '0px' };

export default function AppShell({
  title = 'Workspace',
  sectionLabel = 'Workspace',
  children,
}) {
  const [navOpen, setNavOpen] = useState(false);
  const { mode, cycle } = useSidebarMode();
  const location = useLocation();

  // Close the drawer whenever the route changes
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <div
      className="min-h-screen lg:grid"
      style={{
        gridTemplateColumns: `${MODE_WIDTH[mode] || MODE_WIDTH.open} 1fr`,
        transition: 'grid-template-columns 200ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {/* Static sidebar — desktop only.
          The wrapper (not the aside) is sticky: position:sticky cannot
          engage inside an overflow-hidden ancestor, and overflow-hidden
          must stay here to clip the rail during the width transition. */}
      <div className="hidden lg:block lg:sticky lg:top-0 orin-sidebar-col overflow-hidden">
        {mode !== 'closed' && <Sidebar variant={mode === 'icons' ? 'icons' : 'full'} />}
      </div>

      {/* Slide-over drawer — mobile/tablet */}
      {navOpen && (
        <div className="lg:hidden fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label="Navigation">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm orin-anim-fade"
            onClick={() => setNavOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-y-0 left-0 w-[260px] shadow-2xl orin-anim-slide-right">
            <Sidebar />
          </div>
        </div>
      )}

      <main className="min-w-0 flex flex-col">
        <Topbar
          title={title}
          sectionLabel={sectionLabel}
          onMenuClick={() => setNavOpen(true)}
          onSidebarCycle={cycle}
          sidebarMode={mode}
        />
        <div className="flex-1 px-4 sm:px-7 py-6">{children}</div>
      </main>

      {/* First-login onboarding tour (renders nothing once seen) */}
      <OnboardingTour />
    </div>
  );
}
